import { useMutation, useQueryClient, mutationOptions } from '@tanstack/react-query';

import { aiCampaignKeys } from '#emailCampaignFeature/models/aiCampaigns/keys';

import { bulkDeleteBlacklistEntries, createBlacklistEntries, deleteBlacklistEntry } from '../api/contactBlacklistApi';
import {
	createContactList,
	updateContactList,
	deleteContactList,
	addContactsToList,
	removeContactsFromList,
	removeCompaniesFromList,
	fetchListRecipientPosition,
} from '../api/contactListsApi';

import { contactBlacklistKeys, contactBlacklistMutationKeys, contactListsKeys, contactListsMutationKeys } from './keys';

export const createContactListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.create,
		mutationFn: (listData) => createContactList(listData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.list() });
		},
	});

export function useCreateContactList() {
	const qc = useQueryClient();
	return useMutation(createContactListOptions(qc));
}

export const updateContactListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.update,
		mutationFn: ({ id, updates }) => updateContactList({ id, updates }),
		onMutate: async ({ id, updates }) => {
			await queryClient.cancelQueries({ queryKey: contactListsKeys.list() });
			const previousLists = queryClient.getQueryData(contactListsKeys.list());
			queryClient.setQueryData(contactListsKeys.list(), (old) => {
				if (!old) return old;
				return old.map((list) => (list.id === id ? { ...list, ...updates } : list));
			});
			return { previousLists };
		},
		onError: (err, variables, context) => {
			if (context?.previousLists) {
				queryClient.setQueryData(contactListsKeys.list(), context.previousLists);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
		},
	});

export function useUpdateContactList() {
	const qc = useQueryClient();
	return useMutation(updateContactListOptions(qc));
}

export const deleteContactListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.delete,
		mutationFn: (id) => deleteContactList(id),
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: contactListsKeys.list() });
			const previousLists = queryClient.getQueryData(contactListsKeys.list());
			queryClient.setQueryData(contactListsKeys.list(), (old) => {
				if (!old) return old;
				return old.filter((list) => list.id !== id);
			});
			return { previousLists };
		},
		onError: (err, id, context) => {
			if (context?.previousLists) {
				queryClient.setQueryData(contactListsKeys.list(), context.previousLists);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
		},
	});

export function useDeleteContactList() {
	const qc = useQueryClient();
	return useMutation(deleteContactListOptions(qc));
}

// Add contacts to a contact list (unified: source = crm | contacts | leads)
export const addContactsToListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.addContacts,
		mutationFn: ({ contactListId, ...body }) => addContactsToList(contactListId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
			queryClient.invalidateQueries({ queryKey: [...aiCampaignKeys.all, 'pending-contacts'] });
		},
	});

export function useAddContactsToList() {
	const qc = useQueryClient();
	return useMutation(addContactsToListOptions(qc));
}

export const removeContactsFromListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.removeContact,
		mutationFn: ({ listId, scope }) => removeContactsFromList(listId, scope),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
		},
	});

export function useRemoveContactsFromList() {
	const qc = useQueryClient();
	return useMutation(removeContactsFromListOptions(qc));
}

export const removeCompaniesFromListOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactListsMutationKeys.removeCompany,
		mutationFn: ({ listId, scope }) => removeCompaniesFromList(listId, scope),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
		},
	});

export function useRemoveCompaniesFromList() {
	const qc = useQueryClient();
	return useMutation(removeCompaniesFromListOptions(qc));
}

// Contact blacklist

export const createBlacklistEntriesOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactBlacklistMutationKeys.create,
		mutationFn: (body) => createBlacklistEntries(body),
		meta: { suppressGlobalError: true },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactBlacklistKeys.lists() });
		},
	});

export function useCreateBlacklistEntries() {
	const qc = useQueryClient();
	return useMutation(createBlacklistEntriesOptions(qc));
}

export const deleteBlacklistEntryOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactBlacklistMutationKeys.delete,
		mutationFn: (entryId) => deleteBlacklistEntry(entryId),
		meta: { suppressGlobalError: true },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactBlacklistKeys.lists() });
		},
	});

export function useDeleteBlacklistEntry() {
	const qc = useQueryClient();
	return useMutation(deleteBlacklistEntryOptions(qc));
}

export const bulkDeleteBlacklistEntriesOptions = (queryClient) =>
	mutationOptions({
		mutationKey: contactBlacklistMutationKeys.bulkDelete,
		mutationFn: (scope) => bulkDeleteBlacklistEntries(scope),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contactBlacklistKeys.lists() });
		},
	});

export function useBulkDeleteBlacklistEntries() {
	const qc = useQueryClient();
	return useMutation(bulkDeleteBlacklistEntriesOptions(qc));
}

// A read, run imperatively when a search deep link targets a row on another page; never cached
// because the position shifts whenever recipients are added or removed.
export function useListRecipientPosition() {
	return useMutation({
		mutationKey: contactListsMutationKeys.recipientPosition,
		mutationFn: fetchListRecipientPosition,
	});
}
